import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UploadDropzone } from '@components/UploadDropzone';
import { ContextoCargaPadron, type SeleccionCarga } from '@components/ContextoCargaPadron';
import { ColumnMappingModal } from '@components/ColumnMappingModal';
import { EliminarPadronModal } from '@components/EliminarPadronModal';
import {
  confirmPadron,
  descargarPlantillaBlob,
  eliminarPadron,
  listPadrones,
  previewPadron,
  type PreviewResponse,
} from '@lib/services/padrones.service';
import { useAuthStore } from '@lib/store/auth.store';
import { planDeCarga } from '@lib/empresas/plan-de-carga';
import { UserRole, type PadronSummary } from '@lib/types';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import { EstadoPadronBadge } from '@components/EstadoPadronBadge';
import { ESTADO_PADRON, ESTADOS_PADRON } from '@lib/estado-padron';
import { contarPorEstado, filtrarPadrones, type FiltroEstado } from '@lib/filtrar-padrones';

/**
 * Por qué la tabla quedó vacía. Sale a una función para no anidar ternarios en
 * el JSX (Sonar S3358) y para poder leer las tres variantes de un vistazo.
 */
function mensajeSinResultados(estado: FiltroEstado, busqueda: string): string {
  const q = busqueda.trim();
  const label = estado ? ESTADO_PADRON[estado].label : '';
  if (estado && q) return `Ningún padrón en estado "${label}" que coincida con "${q}".`;
  if (estado) return `No hay ningún padrón en estado "${label}".`;
  return `Ningún padrón coincide con "${q}".`;
}

function errorMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { error?: { message?: string; details?: Array<{ message: string }> } } };
  };
  const data = e.response?.data?.error;
  let msg = data?.message ?? 'Error al procesar el archivo';
  if (data?.details && data.details.length > 0) {
    msg += ' — ' + data.details.map((d) => d.message).join(', ');
  }
  return msg;
}

export function PadronesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, empresas } = useAuthStore();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [eliminar, setEliminar] = useState<PadronSummary | null>(null);
  const [eliminarError, setEliminarError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>(null);
  const [busqueda, setBusqueda] = useState('');

  const padrones = useQuery({
    queryKey: ['padrones'],
    queryFn: listPadrones,
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => eliminarPadron(id),
    onSuccess: () => {
      setEliminar(null);
      setEliminarError(null);
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
    },
    onError: (err: unknown) => setEliminarError(errorMessage(err)),
  });

  const esSuperAdmin = user?.rol === UserRole.SUPERADMIN_MEDICUS;
  const esUsuarioEmpresa = user?.rol === UserRole.USUARIO_EMPRESA;
  // Con qué organización y plan entra el padrón. Lo resuelve el componente de
  // arriba del dropzone; el server lo vuelve a validar al confirmar.
  const [carga, setCarga] = useState<SeleccionCarga | null>(null);

  const previewMutation = useMutation({
    // Con la misma organización que va a ir en el confirm: el server cachea
    // el archivo por organización y rechaza el confirm si difieren.
    mutationFn: (file: File) => previewPadron(file, carga ?? undefined),
    onSuccess: (data) => {
      setUploadError(null);
      setConfirmError(null);
      setPreview(data);
    },
    onError: (err: unknown) => setUploadError(errorMessage(err)),
  });

  // Filtrado en el cliente: `listPadrones` ya trae la lista completa (112 al
  // 2026-08-24, uno por archivo subido) y no está paginada, así que filtrar acá
  // es instantáneo y no agrega un round-trip por tecleo.
  const todos = padrones.data ?? [];
  const visibles = filtrarPadrones(todos, { estado: filtroEstado, busqueda });
  const counts = contarPorEstado(todos);
  const hayFiltro = filtroEstado !== null || busqueda.trim() !== '';
  const limpiarFiltro = () => {
    setFiltroEstado(null);
    setBusqueda('');
  };

  const confirmMutation = useMutation({
    mutationFn: (mapping: Record<string, number>) =>
      confirmPadron(preview!.uploadToken, mapping, carga ?? undefined),
    onSuccess: (padron) => {
      setPreview(null);
      setConfirmError(null);
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
      navigate(`/padrones/${padron.id}`);
    },
    onError: (err: unknown) => setConfirmError(errorMessage(err)),
  });

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Padrones</h1>
        <p className="text-element-mid">
          Cargá un archivo Excel/CSV con el padrón. Validamos formato, catálogos y consistencia del
          grupo.
        </p>
      </header>

      {/* Quién puede cargar lo dice GET /perfil (por rol o sumado por Binary),
          no el rol a secas: un USER_MEDICUS habilitado ve la card, y quien no
          puede ve el motivo en vez de nada. */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="rowBetween">
          <h2 className="heading-5">Subir nuevo padrón</h2>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              descargarPlantillaBlob().catch((err: unknown) => {
                console.error('Error al descargar la plantilla', err);
                window.alert('No se pudo descargar la plantilla. Probá de nuevo en unos segundos.');
              });
            }}
          >
            Descargar plantilla
          </button>
        </div>
        <ContextoCargaPadron seleccion={carga} onChange={setCarga} />
        <UploadDropzone
          disabled={!carga || previewMutation.isPending || confirmMutation.isPending}
          onFile={(file) => {
            setUploadError(null);
            previewMutation.mutate(file);
          }}
        />
        {previewMutation.isPending && (
          <p className="status status-info" style={{ marginTop: '1rem' }}>
            Leyendo archivo…
          </p>
        )}
        {uploadError && (
          <p className="status status-error" style={{ marginTop: '1rem' }}>
            {uploadError}
          </p>
        )}
      </div>

      {preview && (
        <ColumnMappingModal
          preview={preview}
          submitting={confirmMutation.isPending}
          serverError={confirmError}
          onCancel={() => {
            setPreview(null);
            setConfirmError(null);
          }}
          onConfirm={(mapping) => confirmMutation.mutate(mapping)}
        />
      )}

      {eliminar && (
        <EliminarPadronModal
          nombre={eliminar.nombreArchivo}
          estado={eliminar.estado}
          submitting={eliminarMutation.isPending}
          error={eliminarError}
          onCancel={() => {
            setEliminar(null);
            setEliminarError(null);
          }}
          onConfirm={() => eliminarMutation.mutate(eliminar.id)}
        />
      )}

      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="rowBetween">
          <h2 className="heading-5">Padrones cargados</h2>
          {todos.length > 0 && (
            <span className="text-element-mid" style={{ fontSize: 13 }}>
              {hayFiltro ? `${visibles.length} de ${todos.length}` : `${todos.length}`}
            </span>
          )}
        </div>

        {todos.length > 0 && (
          <PadronesFiltro
            counts={counts}
            estado={filtroEstado}
            busqueda={busqueda}
            onEstado={setFiltroEstado}
            onBusqueda={setBusqueda}
          />
        )}

        {padrones.isLoading && <TableSkeleton rows={8} />}
        {padrones.isError && <p className="status status-error">Error al cargar padrones.</p>}
        {padrones.data && padrones.data.length === 0 && (
          <p className="text-element-mid">Aún no hay padrones cargados.</p>
        )}
        {todos.length > 0 && visibles.length === 0 && (
          // Decir POR QUÉ no hay nada: el disparador de esta pantalla fue buscar
          // padrones "pendiente de validación" y no poder distinguir "no existe
          // ninguno" de "no lo estoy encontrando".
          <p className="status status-info" style={{ marginTop: '1rem' }}>
            {mensajeSinResultados(filtroEstado, busqueda)}
            {filtroEstado === 'pendiente_validacion' && (
              <>
                {' '}
                Ojo: un padrón <strong>cerrado</strong> puede tener legajos pendientes de validación
                adentro — se validan desde la pestaña Legajos del padrón.
              </>
            )}{' '}
            <button className="btn btn-link" onClick={limpiarFiltro}>
              Limpiar filtro
            </button>
          </p>
        )}
        {visibles.length > 0 && (
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Subida</th>
                  <th>Plan</th>
                  <th>Total</th>
                  <th>OK</th>
                  <th>Con error</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((p) => (
                  <tr key={p.id}>
                    <td>{p.nombreArchivo}</td>
                    <td>{new Date(p.fechaSubida).toLocaleString('es-AR')}</td>
                    <td
                      style={{ fontFamily: 'monospace', fontSize: 12.5 }}
                      title={planDeCarga(p, empresas).etiqueta}
                    >
                      {planDeCarga(p, empresas).codigo ?? '—'}
                      {!p.planCodigo && (
                        <span className="text-element-mid" style={{ fontFamily: 'inherit' }}>
                          {' '}
                          (base)
                        </span>
                      )}
                      {p.planCodigo && p.sucursalId && (
                        // La sucursal distingue dos padrones del mismo plan.
                        <span className="text-element-mid" style={{ fontFamily: 'inherit' }}>
                          {' '}
                          · suc {p.sucursalId}
                        </span>
                      )}
                    </td>
                    <td>{p.totalRegistros}</td>
                    <td>
                      <span className="status status-success">{p.registrosOk}</span>
                    </td>
                    <td>
                      {p.registrosError > 0 ? (
                        <span className="status status-error">{p.registrosError}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <EstadoPadronBadge estado={p.estado} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Link to={`/padrones/${p.id}`} className="btn btn-link btn-link--outline">
                        Ver detalle
                      </Link>
                      {((esSuperAdmin && p.estado === 'borrador') ||
                        (esUsuarioEmpresa && p.estado === 'borrador')) && (
                        <button
                          className="btn btn-link btn-link--danger"
                          onClick={() => {
                            setEliminarError(null);
                            setEliminar(p);
                          }}
                          style={{ marginLeft: 6 }}
                          title="Eliminar este borrador"
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Filtro de la lista: chips por estado (con el conteo de cada uno, incluidos los
 * que están en cero) y búsqueda por nombre de archivo. Los estados salen de
 * `ESTADOS_PADRON`, no de una lista propia, así que no se puede desincronizar
 * del badge de la tabla.
 */
function PadronesFiltro({
  counts,
  estado,
  busqueda,
  onEstado,
  onBusqueda,
}: {
  counts: Record<string, number>;
  estado: FiltroEstado;
  busqueda: string;
  onEstado: (e: FiltroEstado) => void;
  onBusqueda: (v: string) => void;
}) {
  const opciones: Array<{ key: FiltroEstado; label: string; title?: string }> = [
    { key: null, label: 'Todos' },
    ...ESTADOS_PADRON.map((e) => ({
      key: e as FiltroEstado,
      label: ESTADO_PADRON[e].label,
      title: ESTADO_PADRON[e].descripcion,
    })),
  ];
  return (
    <div
      style={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        flexWrap: 'wrap',
        margin: '0.75rem 0 1rem',
      }}
    >
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {opciones.map((o) => {
          const activo = o.key === estado;
          const n = counts[o.key ?? 'todos'] ?? 0;
          return (
            <button
              key={o.key ?? 'todos'}
              type="button"
              className={activo ? 'btn btn-primary' : 'btn btn-ghost'}
              title={o.title}
              onClick={() => onEstado(o.key)}
              style={{ fontSize: 13, padding: '0.35rem 0.75rem' }}
            >
              {o.label} <span style={{ opacity: 0.7 }}>({n})</span>
            </button>
          );
        })}
      </div>
      <input
        type="search"
        className="input"
        placeholder="Buscar por nombre de archivo…"
        value={busqueda}
        onChange={(e) => onBusqueda(e.target.value)}
        style={{ flex: '1 1 260px', minWidth: 220, maxWidth: 420 }}
      />
    </div>
  );
}
